import { CBSocketClient, SocketClient } from "cbcore-ts"
import { YES } from "uicore-ts"
import { ProcurementQuestionAndAnswerThread, RHMergedQAContentSnapshot, RHQAContentSnapshot } from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHFeatureFlags } from "./RHFeatureFlags"
import { RHSubscriptionBase } from "./RHSubscriptionBase"


// ============================================================================
// RHQAStore
//
// Central store for Q&A questions. Views never call SocketClient directly for
// question operations — they go through this store instead.
//
// ## Cache
// Questions are cached per procurement and active company. The cache is
// invalidated when the server pushes ProcurementQuestionsDidChange.
//
// ## Subscription (inherited from RHSubscriptionBase)
//   store.subscribe(this, (procurementId: string) => { ... })
//   store.unsubscribe(this)
//
// Subscribe in wasAddedToViewTree, unsubscribe in wasRemovedFromViewTree (for
// views), or viewWillAppear / viewWillDisappear (for view controllers).
// ============================================================================

export class RHQAStore extends RHSubscriptionBase<(procurementId: string) => void> {
    
    _questionsByProcurementAndCompanyKey = new Map<string, RHQAContentSnapshot>()
    _mergedQuestionsByRootProcurementAndCompanyKey = new Map<string, RHMergedQAContentSnapshot>()


    constructor(private _core: RHCore) {
        super()
        // Registered on the current socket client AND re-registered on every
        // socket client replacement (login/logout reconnect) — otherwise the
        // store would stop receiving pushes after an in-page login.
        this._registerSocketListeners()
        this._core._functionsToCallForEachSocketClient.push(() => this._registerSocketListeners())
    }


    // =========================================================================
    // Read API
    // =========================================================================
    
    /**
     * Returns the questions for a procurement, fetching from the server on
     * first access and on every access after the cache has been invalidated
     * by a push message.
     */
    async retrieveQuestions(procurementId: string): Promise<RHQAContentSnapshot> {
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const cacheKey = `${procurementId}:${companyId ?? ""}`
        const cached = this._questionsByProcurementAndCompanyKey.get(cacheKey)
        if (cached) {
            return cached
        }
        
        const result = (await SocketClient.RetrieveProcurementQuestions({
            procurementId,
            companyId,
            includeNotificationSnapshot: YES
        })).result
        let snapshot: RHQAContentSnapshot
        if (Array.isArray(result)) {
            snapshot = { questions: result, notificationSnapshotAt: "" }
        }
        else {
            snapshot = result
        }
        
        this._questionsByProcurementAndCompanyKey.set(cacheKey, snapshot)
        return snapshot
    }


    /**
     * Teabevahetus merged across the root procurement and every reconciliation
     * round the caller may see — see RHMergedQAContentSnapshot. `rootProcurementId`
     * must be the root, not a round (a round's own thread list still comes
     * from retrieveQuestions).
     */
    async retrieveMergedQuestions(rootProcurementId: string): Promise<RHMergedQAContentSnapshot> {
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const cacheKey = `${rootProcurementId}:${companyId ?? ""}`
        const cached = this._mergedQuestionsByRootProcurementAndCompanyKey.get(cacheKey)
        if (cached) {
            return cached
        }

        const response = await SocketClient.RetrieveMergedProcurementQuestions({
            procurementId: rootProcurementId,
            companyId
        })
        if (!response.result) {
            const fallback = "Teabevahetuse laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }

        this._mergedQuestionsByRootProcurementAndCompanyKey.set(cacheKey, response.result)
        return response.result
    }


    async _activeCompanyIdForExplicitCompanyCalls(): Promise<string | undefined> {
        if (RHFeatureFlags.multiCompanyAffiliationIsEnabled !== YES) {
            return undefined
        }
        return this._core.companyAffiliationStore.activeCompanyIdAfterLoadingAffiliations()
    }


    async submitQuestion(input: {
        procurementId: string
        bodyText: string
        autoRegisterForProcurement: boolean
        fileIds: string[]
    }): Promise<ProcurementQuestionAndAnswerThread> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.SubmitProcurementQuestion({ ...input, offererCompanyId })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Viga küsimuse esitamisel"
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.question) {
            throw new Error("Viga küsimuse esitamisel")
        }
        this.invalidate(input.procurementId)
        this._notifySubscribers(input.procurementId)
        return response.result.question
    }

    
    async answerQuestion(input: {
        questionId: string
        answerText: string
        answerFileIds: string[]
        procurementId?: string
    }): Promise<ProcurementQuestionAndAnswerThread> {
        const response = await SocketClient.AnswerProcurementQuestion({
            questionId: input.questionId,
            answerText: input.answerText,
            answerFileIds: input.answerFileIds
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Viga vastuse esitamisel"
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.question) {
            throw new Error("Viga vastuse esitamisel")
        }
        if (input.procurementId) {
            this.invalidate(input.procurementId)
            this._notifySubscribers(input.procurementId)
        }
        return response.result.question
    }

    
    async sendProcurerBroadcastMessage(input: {
        procurementId: string
        bodyText: string
        fileIds: string[]
    }): Promise<ProcurementQuestionAndAnswerThread> {
        const response = await SocketClient.SendProcurerBroadcastMessageForProcurement(input)
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Viga sõnumi saatmisel"
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.message) {
            throw new Error("Viga sõnumi saatmisel")
        }
        this.invalidate(input.procurementId)
        this._notifySubscribers(input.procurementId)
        return response.result.message
    }


    // =========================================================================
    // Cache invalidation — called by socket push listener
    // =========================================================================
    
    /**
     * Invalidates the cached questions for the given procurement and notifies
     * all subscribers so they can reload. Called when the server pushes
     * ProcurementQuestionsDidChange.
     */
    _onQuestionsDidChange(procurementId: string) {
        this.invalidate(procurementId)
        this._notifySubscribers(procurementId)
    }
    
    
    /**
     * Clears the cached questions for a procurement without notifying
     * subscribers. Use this before an explicit reload call so the next
     * retrieveQuestions() goes to the server rather than returning the
     * stale cached value. The reload itself will update subscribers via
     * the normal flow once loadQuestionsForProcurement completes.
     */
    invalidate(procurementId: string) {
        const procurementKeyPrefix = `${procurementId}:`
        for (const cacheKey of this._questionsByProcurementAndCompanyKey.keys()) {
            if (cacheKey.startsWith(procurementKeyPrefix)) {
                this._questionsByProcurementAndCompanyKey.delete(cacheKey)
            }
        }
        // A merged snapshot is keyed by its root, but its content spans every
        // visible round too — a change on ANY procurement in that tree (root
        // or round) can affect it, so there's no cheap prefix match here.
        // Merged reads are infrequent enough that clearing the whole cache is
        // simpler and safer than tracking which roots each id belongs to.
        this._mergedQuestionsByRootProcurementAndCompanyKey.clear()
    }
    
    
    /** Resets all cached questions. Call on logout so the next login never sees a stale user's data. */
    clear() {
        this._questionsByProcurementAndCompanyKey.clear()
        this._mergedQuestionsByRootProcurementAndCompanyKey.clear()
    }


    // =========================================================================
    // Socket push listener
    // =========================================================================
    
    private _registerSocketListeners() {
        const socketClient = this._core.socketClient
        
        socketClient.addTargetForMessagesForKey(
            "ProcurementQuestionsDidChange",
            (payload: { procurementId: string }) => {
                if (payload?.procurementId) {
                    this._onQuestionsDidChange(payload.procurementId)
                }
            }
        )
    }
    
}
